import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { QUEST_DATABASE } from '../../../../../shared/content/generated/quests.gen';
import { NPC_CONFIG } from '../../../../../shared/content/generated/entities.gen';
import { ZONE_CONFIGS } from '../../../../../shared/content/generated/zones.gen';
import type { QuestStepState } from '../../../../../shared/quests/questChain';
import { CodexStepIcon } from './CodexOrnaments';
import { LevelRegistry } from '../../../../systems/map/LevelRegistry';
import { placeTooltip, type TooltipPlace } from '../../../components/tooltipPlacement';
// The journal draws this path too, so it brings its own styles rather than relying on the codex page.
import './questCodex.css';

const STEP_LABEL: Record<QuestStepState, string> = {
  done: 'done', active: 'in progress', next: 'next', locked: 'locked',
};

/** The name of the map an NPC stands on, as the game shows it (the level's name; the id only as a fallback). */
export function getNpcLocationName(npcId: string): string | undefined {
  for (const [zoneId, zone] of Object.entries(ZONE_CONFIGS)) {
    if (zone.npcSpawns?.some(s => s.npcId === npcId)) {
      return LevelRegistry.getInstance().getLevel(zoneId)?.name?.trim()
        || zoneId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    }
  }
  return undefined;
}

interface QuestPathProps {
  /** Every prerequisite before the featured quest, root first (hydrated from the quest log). */
  steps: ReadonlyArray<{ id: string; state: QuestStepState }>;
}

/**
 * Indices of steps that end a visual row (their successor wrapped below).
 * `tops` are the steps' offsetTop values in order; a few px of tolerance
 * absorbs baseline jitter between pills on the same row.
 */
export function rowEndIndices(tops: readonly number[], tolerance = 4): number[] {
  const ends: number[] = [];
  for (let i = 0; i < tops.length - 1; i++) {
    if (tops[i + 1] - tops[i] > tolerance) ends.push(i);
  }
  return ends;
}

/**
 * Measures where the flex row wraps so a step that ends a row can curl its
 * vine down toward the next row instead of pointing at the empty margin.
 * Re-measures whenever the list resizes (window width, font load).
 */
function useRowEnds(listRef: React.RefObject<HTMLOListElement | null>, count: number): ReadonlySet<number> {
  const [ends, setEnds] = useState<ReadonlySet<number>>(() => new Set());
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;
    const measure = () => {
      const tops = Array.from(list.children, (child) => (child as HTMLElement).offsetTop);
      const next = rowEndIndices(tops);
      setEnds((prev) => (prev.size === next.length && next.every((i) => prev.has(i)) ? prev : new Set(next)));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => observer.disconnect();
  }, [listRef, count]);
  return ends;
}

/** A step's preview, drawn over the page: the journal's reading pane scrolls, and would cut it off. */
const StepPreview: React.FC<{ anchor: HTMLElement; children: React.ReactNode }> = ({ anchor, children }) => {
  const box = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<TooltipPlace | null>(null);
  useLayoutEffect(() => {
    const drawn = box.current;
    if (!drawn) return;
    setPlace(placeTooltip({ anchor: anchor.getBoundingClientRect() },
      { width: drawn.offsetWidth, height: drawn.offsetHeight }, { width: window.innerWidth, height: window.innerHeight }));
  }, [anchor]);
  return createPortal(
    <div ref={box} className="npc-codex-step-tooltip" role="tooltip" style={place ?? { visibility: 'hidden' }}>{children}</div>,
    document.body,
  );
};

/**
 * The chain of simpler quests that leads to a featured quest, as vine-linked
 * steps marked done, in progress, next or locked (plan 10 §2.2). The path comes
 * from the quests' prerequisites alone (every branch of a "do X and Y first"
 * gate, in dependency order); it shows the way, it unlocks nothing.
 */
export const QuestPath: React.FC<QuestPathProps> = ({ steps }) => {
  const listRef = useRef<HTMLOListElement>(null);
  const rowEnds = useRowEnds(listRef, steps.length);
  const [hovered, setHovered] = useState<{ id: string; step: HTMLElement } | null>(null);

  if (steps.length === 0) return null;
  const doneCount = steps.filter(s => s.state === 'done').length;
  return <section className="npc-codex-path" aria-label="The path here">
    <header className="npc-codex-path-header">
      <span className="npc-codex-path-title">The path here</span>
      <span className="npc-codex-path-count">{doneCount} of {steps.length} walked</span>
    </header>
    <ol className="npc-codex-steps" ref={listRef}>
      {steps.map(({ id, state }, index) => {
        const def = QUEST_DATABASE[id];
        const isHovered = hovered?.id === id;
        return (
          <li
            key={id}
            className="npc-codex-step"
            data-step-state={state}
            data-row-end={rowEnds.has(index) ? 'true' : undefined}
            onMouseEnter={e => setHovered({ id, step: e.currentTarget })}
            onMouseLeave={() => setHovered(null)}
            // Keyboard focus reveals the same preview; Escape or leaving closes it.
            onFocus={e => setHovered({ id, step: e.currentTarget })}
            onBlur={() => setHovered(null)}
            onKeyDown={e => { if (e.key === 'Escape' && isHovered) { e.stopPropagation(); setHovered(null); } }}
            tabIndex={0}
            aria-label={`${def?.name ?? id}: ${STEP_LABEL[state]}`}
          >
            <CodexStepIcon state={state} />
            <span>{def?.name ?? id}</span>
            {isHovered && (() => {
              const npcId = state === 'active' ? (def?.endNpc ?? def?.startNpc) : def?.startNpc;
              const npc = npcId ? NPC_CONFIG[npcId] : undefined;
              const npcName = npc?.name ?? npcId;
              const locationName = npcId ? getNpcLocationName(npcId) : undefined;
              const roleLabel = state === 'done' ? 'Completed with' : state === 'active' ? 'Turn in to' : 'Offered by';

              return (
                <StepPreview anchor={hovered.step}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 6, marginBottom: 4 }}>
                    <strong style={{ color: '#ffd700', fontSize: 12 }}>{def?.name ?? id}</strong>
                    <span style={{ fontSize: 10, textTransform: 'capitalize', color: state === 'done' ? '#42d77d' : state === 'active' || state === 'next' ? '#f59e0b' : '#94a3b8', fontWeight: 600 }}>
                      {STEP_LABEL[state]}
                    </span>
                  </div>
                  {npcName && (
                    <div style={{ fontSize: 10, color: '#93c5fd', marginBottom: 4 }}>
                      {roleLabel}: <span style={{ color: '#e0e7ff', fontWeight: 600 }}>{npcName}</span>
                      {locationName && <span style={{ color: '#94a3b8' }}> ({locationName})</span>}
                    </div>
                  )}
                  {def?.requiredLevel && (
                    <div style={{ fontSize: 10, color: 'rgba(232, 226, 212, 0.75)', marginBottom: 4 }}>
                      Requires Level {def.requiredLevel}
                    </div>
                  )}
                  {def?.description && (
                    <p style={{ margin: 0, fontSize: 11, color: '#e8e2d4', lineHeight: 1.35 }}>
                      {def.description}
                    </p>
                  )}
                </StepPreview>
              );
            })()}
          </li>
        );
      })}
    </ol>
  </section>;
};
